/**
 * Cached API Service
 * Wraps existing API with intelligent caching for faster page loads
 * SAFE: Does not modify original API, only adds caching layer
 */

import { api, endpoints, amazonApi as originalAmazonApi } from './api';
import { dashboardCache, campaignCache, apiCache } from '../utils/apiCache';
import { clearAllLiveEntityCache, invalidateLiveEntityCache } from '../utils/liveEntityCache';
import { clearAllDashboardSessions } from '../utils/dashboardSessionCache';

/**
 * Cached wrapper for GET requests
 * Uses cache for read operations, bypasses for write operations
 */
const cachedGet = async (cacheInstance, endpoint, params = {}, ttl) => {
  const cacheKey = cacheInstance.generateKey(endpoint, params);
  
  // Try cache first
  const cached = cacheInstance.get(cacheKey);
  if (cached) {
    // console.log(`✅ Cache HIT: ${endpoint}`);
    return { data: cached, fromCache: true };
  }
  
  // Cache miss - fetch from API
  // console.log(`❌ Cache MISS: ${endpoint}`);
  const response = await api.get(endpoint, { params });
  
  // Store in cache
  cacheInstance.set(cacheKey, response.data, ttl);
  
  return { ...response, fromCache: false };
};

/**
 * Enhanced Amazon API with caching
 * Wraps original amazonApi calls with smart caching
 * IMPORTANT: Preserves original function signatures for compatibility
 */
export const amazonApi = {
  // Pass through all original methods
  ...originalAmazonApi,
  
  // getCachedReport, getCampaigns, dashboard summary, etc. use liveEntityCache via ...originalAmazonApi
  
  /**
   * Clear cache for specific profile
   * Call this after making updates to ensure fresh data
   */
  clearProfileCache: (profileId) => {
    invalidateLiveEntityCache(profileId, ['all']);
    dashboardCache.clear();
    campaignCache.clear();
    console.log(`🗑️ Cleared cache for profile: ${profileId}`);
  },
  
  /**
   * Clear all caches
   * Call this when user logs out or switches accounts
   */
  clearAllCaches: () => {
    clearAllLiveEntityCache();
    clearAllDashboardSessions();
    dashboardCache.clear();
    campaignCache.clear();
    apiCache.clear();
    console.log('🗑️ Cleared all caches');
  },
};

/**
 * Enhanced API utils with selective caching
 * Only caches GET requests, bypasses cache for write operations
 */
export const cachedApiUtils = {
  /**
   * Cached GET - uses cache
   */
  get: async (url, params = {}, cacheTTL = 3 * 60 * 1000) => {
    return cachedGet(apiCache, url, params, cacheTTL);
  },
  
  /**
   * POST - bypasses cache and clears relevant cache entries
   */
  post: async (url, data = {}) => {
    const response = await api.post(url, data);
    // Clear cache after write operation
    if (url.includes('/campaigns')) {
      campaignCache.clear();
    } else if (url.includes('/dashboard')) {
      dashboardCache.clear();
    }
    return response;
  },
  
  /**
   * PUT - bypasses cache and clears relevant cache entries
   */
  put: async (url, data = {}) => {
    const response = await api.put(url, data);
    // Clear cache after write operation
    if (url.includes('/campaigns')) {
      campaignCache.clear();
    } else if (url.includes('/dashboard')) {
      dashboardCache.clear();
    }
    return response;
  },
  
  /**
   * DELETE - bypasses cache and clears relevant cache entries
   */
  delete: async (url) => {
    const response = await api.delete(url);
    // Clear cache after write operation
    campaignCache.clear();
    dashboardCache.clear();
    return response;
  },
};

/**
 * Hook to clear cache when store changes
 * Use in components that need to invalidate cache on store switch
 */
export const useCacheClear = () => {
  return {
    clearProfileCache: amazonApi.clearProfileCache,
    clearAllCaches: amazonApi.clearAllCaches,
  };
};

export default amazonApi;

